import { useCallback } from 'react';
import { HISTORY_LIMIT } from '../constants/canvas';
import { cloneValue } from '../utils/common';

/**
 * 画布撤销/重做历史管理
 */
export const useCanvasHistory = ({
    nodesRef,
    edgesRef,
    nextImageNodeNumberRef,
    nextGroupNodeNumberRef,
    activeGroupId,
    selectedNodeIds,
    setNodes,
    setEdges,
    setNextImageNodeNumber,
    setNextGroupNodeNumber,
    setActiveGroupId,
    setSelectedNodeIds,
    persistCanvasBatch,
    pasteCountRef,
    historyRef,
    redoHistoryRef,
}) => {

    // 记录当前画布快照，供撤销/重做使用
    const getSnapshot = useCallback(() => cloneValue({
        nodes: nodesRef.current,
        edges: edgesRef.current,
        nextImageNodeNumber: nextImageNodeNumberRef.current,
        nextGroupNodeNumber: nextGroupNodeNumberRef.current,
        activeGroupId,
        selectedNodeIds,
    }), [activeGroupId, nodesRef, edgesRef, nextImageNodeNumberRef, nextGroupNodeNumberRef, selectedNodeIds]);

    // 恢复撤销/重做时保存的画布快照
    const restoreSnapshot = useCallback((snapshot) => {
        setNodes(cloneValue(snapshot.nodes));
        setEdges(cloneValue(snapshot.edges));
        setNextImageNodeNumber(snapshot.nextImageNodeNumber);
        setNextGroupNodeNumber(snapshot.nextGroupNodeNumber ?? 1);
        setActiveGroupId(snapshot.activeGroupId ?? null);
        setSelectedNodeIds(snapshot.selectedNodeIds ?? []);
    }, [setEdges, setNodes, setNextImageNodeNumber, setNextGroupNodeNumber, setActiveGroupId, setSelectedNodeIds]);

    // 推入历史栈，并按需清空重做栈
    const pushHistory = useCallback((snapshot = getSnapshot(), options = {}) => {
        const { clearRedo = true } = options;
        historyRef.current.push(snapshot);
        if (historyRef.current.length > HISTORY_LIMIT) {
            historyRef.current.shift();
        }
        if (clearRedo) {
            redoHistoryRef.current = [];
        }
    }, [getSnapshot, historyRef, redoHistoryRef]);

    // 撤销上一步画布操作
    const handleUndo = useCallback(() => {
        const snapshot = historyRef.current.pop();
        if (!snapshot) return;
        const currentSnapshot = getSnapshot();
        redoHistoryRef.current.push(currentSnapshot);
        restoreSnapshot(snapshot);
        persistCanvasBatch({
            beforeNodes: currentSnapshot.nodes,
            beforeEdges: currentSnapshot.edges,
            afterNodes: snapshot.nodes,
            afterEdges: snapshot.edges,
        });
        pasteCountRef.current = 0;
    }, [getSnapshot, historyRef, persistCanvasBatch, redoHistoryRef, restoreSnapshot, pasteCountRef]);

    // 重做刚刚撤销的画布操作
    const handleRedo = useCallback(() => {
        const snapshot = redoHistoryRef.current.pop();
        if (!snapshot) return;
        const currentSnapshot = getSnapshot();
        pushHistory(currentSnapshot, { clearRedo: false });
        restoreSnapshot(snapshot);
        persistCanvasBatch({
            beforeNodes: currentSnapshot.nodes,
            beforeEdges: currentSnapshot.edges,
            afterNodes: snapshot.nodes,
            afterEdges: snapshot.edges,
        });
        pasteCountRef.current = 0;
    }, [getSnapshot, persistCanvasBatch, pushHistory, redoHistoryRef, restoreSnapshot, pasteCountRef]);

    return {
        historyRef,
        redoHistoryRef,
        getSnapshot,
        restoreSnapshot,
        pushHistory,
        handleUndo,
        handleRedo,
    };
};